import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { ChevronLeft, Send, Mail } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function SignalLetter() {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/signal-letter/preview").then(({ data }) => setPreview(data));
  }, []);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/signal-letter/send", { to: preview?.to || null });
      if (data.status === "sent") toast.success("Sent gently.");
      else if (data.status === "preview") toast("Email delivery is not yet wired — preview only.");
      else toast.error("Couldn’t send right now.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Please opt in first.");
    } finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="px-8 pt-16 pb-6" data-testid="page-signal-letter">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-2">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <p className="font-script text-2xl text-brand">signal letters</p>
        <h1 className="font-serif text-4xl leading-tight">A quiet <em>weekly</em> letter.</h1>
        <p className="mt-3 text-foreground/70 leading-relaxed">
          Soft, literary, opt-in. No streaks, no pressure — just someone quietly rooting for you.
        </p>
      </div>

      <div className="px-8 pb-10 space-y-4">
        {!preview && <div className="rounded-3xl bg-card border border-border p-6 animate-soft-pulse h-40" />}
        {preview && (
          <>
            <div className="rounded-3xl bg-card border border-border overflow-hidden" data-testid="letter-preview">
              <div className="px-5 py-3 border-b border-border flex items-center gap-2 text-xs uppercase tracking-widest text-foreground/55">
                <Mail className="w-3.5 h-3.5" /> preview · {preview.subject}
              </div>
              <iframe
                title="Signal Letter preview"
                className="w-full"
                style={{ height: 580, border: 0, background: "#FDFBF7" }}
                srcDoc={preview.html}
              />
            </div>

            <div className="rounded-3xl bg-card border border-border p-5 text-sm text-foreground/70 leading-relaxed">
              {preview.opted_in ? (
                preview.deliverable_now ? (
                  <>You’re opted in. We’ll send this every week to <strong>{preview.to || "your email"}</strong>.</>
                ) : (
                  <>You’re opted in — when our email service is connected, we’ll send to <strong>{preview.to || "your email"}</strong>.</>
                )
              ) : (
                <>You haven’t opted in yet. Visit <strong>Me → Settings → Signal Letters</strong> when you’re ready.</>
              )}
            </div>

            {preview.opted_in && (
              <Button
                data-testid="letter-send-btn"
                onClick={send}
                disabled={busy}
                className="w-full h-13 rounded-full bg-brand hover:bg-brand/90 text-white"
              >
                <Send className="w-4 h-4 mr-2" />
                {preview.deliverable_now ? "Send this letter now" : "Save (will send when wired)"}
              </Button>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
